// @flow
import React, {Fragment, PureComponent} from 'react';
import Modal from '@homeaway/react-modal';
import {FormattedMessage} from 'react-intl';
import {Query} from 'react-apollo';
import {Button} from '@homeaway/react-buttons';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_RIGHT__16} from '@homeaway/svg-defs';

import MultiUnitTypesSummary from '../MultiUnitTypesSummary/MultiUnitTypesSummary';
import MultiUnitListing from '../MultiUnitListing/MultiUnitListing';
import MultiUnitLoader from '../MultiUnitLoader/MultiUnitLoader';
import logger from '../../../utils/clientLogger';
import {triggerMultipleUnitsClickEDAPEvent} from '../../../utils/hitEdapUtils';
import Timing from '../../Timing/Timing';

import {
    createMultiUnitSearchQuery,
    setVariables,
    setQueryStartTimer,
    setQueryEndTimer,
    clearQueryTimer
} from '../utils/MultiUnitQuery.util';

import type {SearchParamsType} from '../../../reducers/searchParams';
import {response} from '../Mocks/MultiUnitSearchResponse';
import './MultiUnitModal.less';

type Props = {
    inputParams: SearchParamsType,
    systemId: string,
    propertyId: number,
    propertyName: string,
    currency: string,
    isDatelessSearch: boolean,
    isMetric: boolean,
    fullLocale: string,
    mock: string
};

type State = {
    isOpen: boolean
};

const MULTI_UNIT_LABEL = (
    <FormattedMessage
        id="multipleUnitsButtonLabel"
        defaultMessage="Multiple units"
    />
);

const renderMultiUnitLoader = (isOpen, propertyName, scope) => {
    return (
        <Modal
            className="MultiUnit__modal"
            id="multiUnitModal"
            isOpen={isOpen}
            onClose={scope.onModalClose}
            modalAnimation="modal-from-bottom"
            isWorkflow
            aria-label={propertyName}
            title={
                <MultiUnitTypesSummary propertyName={propertyName} loading />
            }
        >
            <ul className="MultiUnitModal__content">
                <MultiUnitLoader multiUnitDataCount={8} />
            </ul>
        </Modal>
    );
};

const renderMultiUnitModal = ({
    isOpen,
    propertyName,
    data,
    isMetric,
    propertyId,
    scope,
    fullLocale,
    isDatelessSearch
}) => {
    return (
        <Modal
            className="MultiUnit__modal"
            id="multiUnitModal"
            isOpen={isOpen}
            onClose={scope.onModalClose}
            modalAnimation="modal-from-bottom"
            isWorkflow
            aria-label={propertyName}
            title={
                <MultiUnitTypesSummary
                    propertyName={propertyName}
                    resultCount={
                        data &&
                        data.multiUnitSearch &&
                        data.multiUnitSearch.resultCount
                    }
                    loading={false}
                />
            }
        >
            <ul className="MultiUnitModal__content">
                <Timing measureName="multiUnitListing">
                    <MultiUnitListing
                        fullLocale={fullLocale}
                        isDatelessSearch={isDatelessSearch}
                        isMetric={isMetric}
                        multiUnitList={data}
                        propertyId={propertyId}
                        context="modal"
                    />
                </Timing>
            </ul>
        </Modal>
    );
};

export default class MultiUnitModal extends PureComponent<Props, State> {
    state = {
        isOpen: false
    };

    onModalOpen = () => {
        triggerMultipleUnitsClickEDAPEvent(this.props);
        this.setState({isOpen: true});
        setQueryStartTimer('multiUnitSearchQuery_mobile');
    };

    onModalClose = () => {
        this.setState({isOpen: false});
        clearQueryTimer('multiUnitSearchQuery_mobile');
    };

    render() {
        const {isOpen} = this.state;
        const {
            inputParams,
            systemId,
            propertyId,
            propertyName,
            currency,
            isMetric,
            fullLocale,
            isDatelessSearch,
            mock
        } = this.props;

        const variables = setVariables(inputParams, systemId, propertyId);
        return (
            <div className="MultiUnitModal">
                <Button
                    label={
                        <Fragment>
                            {MULTI_UNIT_LABEL}
                            <SVGIcon inlineFlex markup={CHEVRON_RIGHT__16} />
                        </Fragment>
                    }
                    size="xs"
                    className="MultiUnit__modal-btn"
                    onClick={this.onModalOpen}
                />
                {isOpen &&
                    ((mock === 'withKitchenSink' &&
                        renderMultiUnitModal({
                            isOpen,
                            propertyName,
                            data: response.data,
                            isMetric,
                            propertyId,
                            fullLocale,
                            isDatelessSearch,
                            scope: this
                        })) || (
                        <Query
                            query={createMultiUnitSearchQuery(currency)}
                            variables={variables}
                            errorPolicy="all"
                        >
                            {({loading, error, data}) => {
                                if (loading) {
                                    return renderMultiUnitLoader(
                                        isOpen,
                                        propertyName,
                                        this
                                    );
                                }

                                setQueryEndTimer('multiUnitSearchQuery_mobile');
                                if (error) {
                                    logger.captureException(
                                        `[MultiUnitSearchQuery error]: ${error}`,
                                        {
                                            extra: `Unable to fetch listing results from graphql-traveler-search for property Id - ${propertyId}`
                                        }
                                    );
                                }

                                return renderMultiUnitModal({
                                    isOpen,
                                    propertyName,
                                    data,
                                    isMetric,
                                    propertyId,
                                    fullLocale,
                                    isDatelessSearch,
                                    scope: this
                                });
                            }}
                        </Query>
                    ))}
            </div>
        );
    }
}
